/**
 * MOTEUR UNIQUE DE CALCUL DES RÉSULTATS (écart M7-P0-1).
 *
 * Avant : quatre formules de moyenne pour la même matière — la délibération (0,5/0,5, note absente comptée
 * zéro, moyenne de lignes brutes), le relevé (poids du tenant, matière sans note ignorée), la réclamation
 * (0,4/0,6, une note absente comptée zéro) et le tableau de bord SQL (0,5/0,5) — d'où un PV et un relevé
 * qui pouvaient se contredire pour un même étudiant.
 *
 * Maintenant : UNE règle, DÉCLARATIVE, lue du modèle académique effectif du tenant et VERSIONNÉE :
 *   · composantes et pondérations : `grading_system.cc_weight` / `sn_weight` (réglages vivants du tenant) ;
 *   · `calcul.seconde_session`   : « remplace » (le rattrapage remplace l'examen) ou « meilleure » ;
 *   · `calcul.note_absente`      : « ignorer » (la matière n'a pas de moyenne) ou « zero » ;
 *   · `calcul.moyenne_generale`  : « simple » (défaut, comportement historique) ou « ponderee_coefficients » ;
 *   · `calcul.decimales`, `calcul.coefficient_par_defaut`, `calcul.compensation.plancher_ratio` ;
 *   · seuil de validation : la note de passage du modèle ; crédits acquis (capitalisation).
 * Aucun barème, aucun pays, aucune convention n'est écrit ici : un tenant d'un autre régime change de règle
 * en changeant de modèle. Le miroir SQL est `public.moyenne_matiere(cc, sn, sr, modele)` (même règle, mêmes cas
 * dans `moteurCalculResultats.test.js` et dans `moteur_calcul_resultats.test.sql`).
 *
 * Chaque résultat garde la version de la règle qui l'a produit : `regleVersion` = `gabarit@version/m<moteur>`.
 * Fonctions pures, zéro dépendance React ni réseau.
 */

/** Version de l'ALGORITHME (pas des données du modèle) : à incrémenter si la façon de calculer change. */
export const VERSION_MOTEUR = 1;

/**
 * Dernier recours quand ni modèle ni réglage du tenant n'est chargé (visiteur anonyme, test sans tenant) :
 * les pondérations par défaut de l'application (`DEFAULT_TENANT`). Jamais utilisé pour un tenant chargé.
 */
const POIDS_DE_DERNIER_RECOURS = { cc: 0.4, sn: 0.6 };

const OPTIONS_PAR_DEFAUT = {
  secondeSession: 'remplace',
  noteAbsente: 'ignorer',
  moyenneGenerale: 'simple',
  decimales: 2,
  coefficientParDefaut: 1,
};

const nombre = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const arrondir = (v, decimales) => {
  const f = 10 ** decimales;
  return Math.round(v * f) / f;
};

/**
 * La règle de calcul d'un tenant, à partir de son objet `tenant` (modèle effectif `modele_academique`,
 * réglages vivants `grading_system`). Sans modèle, la règle se déduit des seuls réglages vivants.
 */
export function regleDeCalcul(tenant) {
  const modele = tenant?.modele_academique || null;
  // Le modèle effectif a déjà absorbé les réglages vivants ; sans modèle (ou sans pondérations dedans), ceux du tenant.
  const gs = modele?.grading_system || tenant?.grading_system || {};
  const avecPoids = [modele?.grading_system, tenant?.grading_system]
    .find((g) => nombre(g?.cc_weight) !== null && nombre(g?.sn_weight) !== null);
  const poids = avecPoids ? { cc: nombre(avecPoids.cc_weight), sn: nombre(avecPoids.sn_weight) } : POIDS_DE_DERNIER_RECOURS;
  const c = modele?.calcul || {};
  const compensation = nombre(c.compensation?.plancher_ratio);
  return {
    ccW: poids.cc,
    snW: poids.sn,
    secondeSession: c.seconde_session || OPTIONS_PAR_DEFAUT.secondeSession,
    noteAbsente: c.note_absente || OPTIONS_PAR_DEFAUT.noteAbsente,
    moyenneGenerale: c.moyenne_generale || OPTIONS_PAR_DEFAUT.moyenneGenerale,
    decimales: nombre(c.decimales) ?? OPTIONS_PAR_DEFAUT.decimales,
    coefficientParDefaut: nombre(c.coefficient_par_defaut) ?? OPTIONS_PAR_DEFAUT.coefficientParDefaut,
    max: nombre(gs.max),
    pass: nombre(gs.pass),
    seuilReussite: nombre(modele?.seuil_reussite) ?? nombre(gs.pass),
    compensationPlancher: compensation,
    regleVersion: `${modele?.gabarit || 'aucun'}@${modele?.version ?? 0}/m${VERSION_MOTEUR}`,
  };
}

/**
 * RÈGLE ACTIVE de la session (comme la devise active) : posée par le TenantContext dès que le modèle du tenant est
 * chargé. Les calculs qui ne reçoivent pas le tenant (taux de réussite des cockpits) en prennent leurs
 * pondérations et leur seuil ; `null` tant qu'aucun tenant n'est chargé.
 */
let regleActiveCourante = null;
export const definirRegleActive = (regle) => { regleActiveCourante = regle || null; };
export const regleActive = () => regleActiveCourante;

/** Pondérations (contrôle continu, examen) de la règle d'un tenant : pour les écrans qui prévisualisent une note. */
export function poidsDeCalcul(tenant) {
  const r = regleDeCalcul(tenant);
  return { ccW: r.ccW, snW: r.snW };
}

/**
 * Moyenne d'une matière.
 * Le rattrapage remplace l'examen (ou donne la meilleure des deux, selon la règle). Une note absente rend
 * `null` (« ignorer ») ou compte zéro (« zero ») ; sans AUCUNE note, jamais un zéro : `null`.
 */
export function moyenneMatiere({ cc, sn, sr } = {}, regle) {
  const r = regle || regleDeCalcul(null);
  const c = nombre(cc);
  const s = nombre(sn);
  const rat = nombre(sr);
  let examen;
  if (rat === null) examen = s;
  else if (s === null) examen = rat;
  else examen = r.secondeSession === 'meilleure' ? Math.max(s, rat) : rat;
  if (c === null && examen === null) return null;
  if ((c === null || examen === null) && r.noteAbsente !== 'zero') return null;
  return arrondir((c ?? 0) * r.ccW + (examen ?? 0) * r.snW, r.decimales);
}

/**
 * Bulletin d'un étudiant : moyennes de matière, moyenne générale, validation (note de passage), compensation
 * (matière sous la note de passage mais au-dessus du plancher, la moyenne générale atteignant la note de passage)
 * et crédits acquis (capitalisation : une matière déjà `acquis` le reste).
 *
 * @param {Array<{cc?, sn?, sr?, coefficient?, credits?, acquis?}>} lignes
 * @returns {{matieres: Array, moyenne: number|null, creditsTotal: number, creditsAcquis: number, regleVersion: string}}
 */
export function calculerBulletin(lignes, regle) {
  const r = regle || regleDeCalcul(null);
  const matieres = (lignes || []).map((l) => {
    const moyenne = moyenneMatiere(l, r);
    const coefficient = nombre(l.coefficient) ?? r.coefficientParDefaut;
    return { ...l, moyenne, coefficient, credits: nombre(l.credits) ?? 0 };
  });
  const notees = matieres.filter((m) => m.moyenne !== null);
  let moyenne = null;
  if (notees.length) {
    if (r.moyenneGenerale === 'simple') {
      moyenne = notees.reduce((s, m) => s + m.moyenne, 0) / notees.length;
    } else {
      const somme = notees.reduce((s, m) => s + m.moyenne * m.coefficient, 0);
      const poids = notees.reduce((s, m) => s + m.coefficient, 0);
      moyenne = poids > 0 ? somme / poids : null;
    }
    if (moyenne !== null) moyenne = arrondir(moyenne, r.decimales);
  }
  const generaleAtteinte = moyenne !== null && r.pass !== null && moyenne >= r.pass;
  const detail = matieres.map((m) => {
    const validee = m.moyenne !== null && r.pass !== null ? m.moyenne >= r.pass : null;
    const plancher = r.compensationPlancher !== null && r.max !== null ? r.compensationPlancher * r.max : null;
    const compensee = validee === false && plancher !== null && m.moyenne >= plancher && generaleAtteinte;
    return { ...m, validee, compensee, acquise: Boolean(m.acquis) || validee === true || compensee };
  });
  return {
    matieres: detail,
    moyenne,
    creditsTotal: detail.reduce((s, m) => s + (m.moyenne !== null ? m.credits : 0), 0),
    creditsAcquis: detail.reduce((s, m) => s + (m.acquise ? m.credits : 0), 0),
    regleVersion: r.regleVersion,
  };
}
